import { Injectable, signal } from '@angular/core';
import { SECRETS } from 'src/environments/secrets';

/**
 * Shape del config easter egg. Definito qui invece che in
 * `secrets.example.ts` per due motivi:
 *   1. `secrets.ts` (gitignored) può NON avere ancora questo campo su
 *      alcune workstation legacy → uso interface locale + type
 *      assertion opzionale con fallback disabled.
 *   2. Il maintainer aggiorna solo il proprio `secrets.ts` con i
 *      valori reali quando vuole abilitare la feature.
 */
interface EasterEggConfig {
  enabled: boolean;
  secretWord: string;
  personalMessage: string;
  gifPath: string;
}

const DEFAULT_EASTER_CONFIG: EasterEggConfig = {
  enabled: false,
  secretWord: '',
  personalMessage: '',
  gifPath: '',
};

/**
 * Hidden UX feature — easter egg del maintainer, KEYBOARD-ONLY.
 *
 * ─── Attivazione ─────────────────────────────────────────────────
 * In qualsiasi punto della webapp (fuori dagli input di form), se
 * l'utente digita la sequenza segreta configurata in
 * `SECRETS.EASTER_EGG.secretWord`, si attiva l'escalation. Ogni
 * digitazione della secretWord fa avanzare al tier successivo.
 * Il click sul logo Diakonix ora apre invece il modale "Credits"
 * (feature pubblica) — separazione netta fra gag privata e UX
 * pubblica.
 *
 * ─── Tier progression ────────────────────────────────────────────
 *   Tier 1 (10s, rosso)    "AUTODISTRUZIONE ATTIVATA"
 *   Tier 2 ( 8s, rosso hard) "NON CI RIPROVARE — non è un gioco"
 *   Tier 3 ( 5s, arancio)  "ULTIMO AVVISO — la prossima volta sei fuori"
 *   Tier 4 ( 3s, viola)    "OK sei sospeso!"
 *                          → suspended (60s con nome utente)
 *                          → GIF + messaggio personale come finale
 *   Tier 5+ (reset a 1)    torna a "AUTODISTRUZIONE"
 *
 * ─── Sicurezza / no risk collega bloccato ───────────────────────
 * Feature attivata SOLO quando:
 *   - `SECRETS.EASTER_EGG.enabled === true` (gitignored config del
 *     maintainer, false ovunque altrove)
 *   - la secretWord è configurata (>0 char)
 *   - l'utente digita quella sequenza specifica fuori da un input
 *
 * Il modale suspended ha sempre un bottone "RILASCIA" che chiude
 * il flow in qualsiasi momento — nessun blocco reale della sessione,
 * dei dati, della route. Puro UI teatrale.
 *
 * ─── Persistence ─────────────────────────────────────────────────
 * Il contatore di escalation vive in `sessionStorage` — sopravvive
 * al reload della stessa tab, non è cross-tab, muore alla chiusura
 * del browser. Coerente con l'intento "gag di sessione".
 *
 * State machine:
 *   idle → armed (tier N countdown) → boom (5s) ────→ joke (2.5s) → idle
 *                                              ↘
 *                                        suspended (60s) → secret (10s) → idle
 *                                        [tier 4 only]
 */
export type EasterEggState =
  | 'idle'
  | 'armed'
  | 'boom'
  | 'joke'
  | 'suspended'
  | 'secret';

/** Descrittore di un tier di escalation. */
interface EasterTier {
  id: 1 | 2 | 3 | 4;
  /** Durata del countdown in secondi. */
  duration: number;
  /** Messaggio principale nel modale armed. */
  header: string;
  /** Sottotitolo autoironico sotto il countdown. */
  subtext: string;
  /** Variante colore CSS (per il data-attribute nel template). */
  variant: 'red' | 'red-hard' | 'orange' | 'purple';
  /** Se true, dopo il boom va a `suspended` invece che `joke`. */
  goesToSuspended: boolean;
  /** Testo del bottone annulla — cambia per tono. */
  cancelLabel: string;
}

const TIERS: readonly EasterTier[] = [
  {
    id: 1,
    duration: 10,
    header: '⚠️ SISTEMA AUTODISTRUZIONE ATTIVATO',
    subtext: 'Autodistruzione in',
    variant: 'red',
    goesToSuspended: false,
    cancelLabel: 'ANNULLA — Ho paura del cuore 💔',
  },
  {
    id: 2,
    duration: 8,
    header: '🚨 NON CI RIPROVARE — NON È UN GIOCO',
    subtext: 'Detonazione in',
    variant: 'red-hard',
    goesToSuspended: false,
    cancelLabel: 'ANNULLA — Prometto di essere buono 🙏',
  },
  {
    id: 3,
    duration: 5,
    header: '⚡ ULTIMO AVVISO — LA PROSSIMA VOLTA SEI FUORI',
    subtext: 'Cancellazione account in',
    variant: 'orange',
    goesToSuspended: false,
    cancelLabel: 'ANNULLA — Non lo faccio più, promesso 😰',
  },
  {
    id: 4,
    duration: 3,
    header: '💀 OK, TE L\'AVEVO DETTO — PAUSA FORZATA',
    subtext: 'Sospensione in',
    variant: 'purple',
    goesToSuspended: true,
    cancelLabel: 'INEVITABILE',
  },
] as const;

/**
 * Durata del modale "suspended" in secondi (tier 4 only).
 * v2.14.2: era 30s nella prima proposta, portato a 60s su feedback
 * maintainer (feature keyboard-only → solo lui la vede → zero
 * rischio bloccare colleghi, quindi timer lungo autoironico).
 */
const SUSPENDED_DURATION_SECONDS = 60;

/** Durata della GIF personale finale (tier 4, dopo suspended). */
const SECRET_FINALE_DURATION_MS = 10_000;

/** Chiave sessionStorage per il contatore di escalation. */
const STORAGE_KEY = 'diakonix.ee.click_count';

@Injectable({ providedIn: 'root' })
export class EasterEggService {
  /** Signal reattivo dello state per il modale template. */
  readonly state = signal<EasterEggState>('idle');

  /** Countdown remaining (N → 0) durante `armed` state. */
  readonly countdown = signal<number>(10);

  /** Countdown remaining (60 → 0) durante `suspended` state (tier 4). */
  readonly suspendedCountdown = signal<number>(SUSPENDED_DURATION_SECONDS);

  /** Tier corrente (per il template — messaggio, colore, ecc.). */
  readonly currentTier = signal<EasterTier>(TIERS[0]);

  /** Nome utente per il modale suspended ("<nome> sospeso!"). */
  readonly userName = signal<string>('');

  /** Buffer keyboard accumulato per matchare secretWord (max 32 char). */
  private keyBuffer = '';
  private keyListener: ((e: KeyboardEvent) => void) | null = null;

  /** Timer handles per cleanup. */
  private countdownTimer: number | null = null;
  private phaseTimer: number | null = null;
  private suspendedTimer: number | null = null;

  /**
   * Config easter egg da `secrets.ts` (gitignored). Type assertion
   * opzionale con fallback disabled → funziona sia se il campo è
   * presente sia se no (compat con secrets.ts legacy).
   */
  private readonly config: EasterEggConfig =
    (SECRETS as unknown as { EASTER_EGG?: EasterEggConfig }).EASTER_EGG
    ?? DEFAULT_EASTER_CONFIG;

  /**
   * Attiva il listener globale della tastiera. Chiamato una sola
   * volta da `AppComponent.ngOnInit`.
   *
   * No-op se `enabled === false` (nessuna secretWord configurata)
   * — evita listener inutili per il 99% degli utenti.
   */
  initialize(): void {
    if (!this.hasSecretMode()) return;
    this.attachKeyboardListener();
  }

  /**
   * Cleanup del listener globale. Chiamato da
   * `AppComponent.ngOnDestroy`. Idempotente.
   */
  destroy(): void {
    this.detachKeyboardListener();
    this.cleanup();
  }

  /**
   * Annulla il flow corrente (bottone "annulla" nel modale armed,
   * o "rilascia" nel suspended). Torna a idle immediatamente.
   *
   * NOTA: cancel NON decrementa il contatore. Aver digitato la
   * secretWord *conta* come tentativo. Sadico ma equo.
   */
  cancel(): void {
    this.cleanup();
    this.state.set('idle');
  }

  /**
   * Reset esplicito del contatore di escalation. Chiamato dal
   * bottone di rilascio o come utility da developer console per
   * ricominciare da tier 1.
   */
  resetEscalation(): void {
    this.writeClickCount(0);
  }

  /** Verifica se la modalità segreta è configurata + attiva. */
  hasSecretMode(): boolean {
    return this.config.enabled === true && this.config.secretWord.length > 0;
  }

  /** Getter pubblici usati dal template. */
  personalMessage(): string {
    return this.config.personalMessage;
  }
  gifPath(): string {
    return this.config.gifPath;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Trigger — invocato quando il keyboard listener matcha la secretWord
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Avanza al prossimo tier di escalation. Chiamato internamente
   * quando la secretWord è matchata nel buffer keyboard.
   *
   * No-op se già in stato non-idle (secondo trigger consecutivo
   * viene ignorato — un modale alla volta).
   *
   * @param userName facoltativo, mostrato nel modale suspended
   *                 (tier 4). Formato libero — al momento il
   *                 keyboard listener non ha accesso al nome
   *                 dell'utente, quindi resta placeholder o vuoto.
   */
  private arm(userName?: string): void {
    if (this.state() !== 'idle') return;

    const nextClickIndex = this.readClickCount();
    const tierIdx = nextClickIndex % TIERS.length;
    this.writeClickCount(nextClickIndex + 1);
    const tier = TIERS[tierIdx];

    this.currentTier.set(tier);
    this.userName.set(userName?.trim() || 'Maintainer');
    this.state.set('armed');
    this.countdown.set(tier.duration);
    this.startCountdown();
  }

  // ─────────────────────────────────────────────────────────────────────
  // Internal state machine
  // ─────────────────────────────────────────────────────────────────────

  private startCountdown(): void {
    this.countdownTimer = window.setInterval(() => {
      const next = this.countdown() - 1;
      this.countdown.set(next);
      if (next <= 0) {
        this.stopCountdown();
        this.enterBoom();
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private enterBoom(): void {
    this.state.set('boom');
    const tier = this.currentTier();
    this.phaseTimer = window.setTimeout(() => {
      if (tier.goesToSuspended) {
        this.enterSuspended();
      } else {
        this.enterJoke();
      }
    }, 5000);
  }

  private enterJoke(): void {
    this.state.set('joke');
    this.phaseTimer = window.setTimeout(() => this.finish(), 2500);
  }

  /**
   * Tier 4 finale — parte 1: "sospensione" autoironica di 60s.
   * L'utente può chiudere in qualsiasi momento — nessun blocco reale.
   *
   * Al termine del timer (o alla chiusura manuale) transiziona a
   * `secret` per mostrare la GIF + messaggio personale come "reveal"
   * finale del percorso completo.
   */
  private enterSuspended(): void {
    this.state.set('suspended');
    this.suspendedCountdown.set(SUSPENDED_DURATION_SECONDS);
    this.suspendedTimer = window.setInterval(() => {
      const next = this.suspendedCountdown() - 1;
      this.suspendedCountdown.set(next);
      if (next <= 0) {
        this.stopSuspended();
        this.enterSecretFinale();
      }
    }, 1000);
  }

  private stopSuspended(): void {
    if (this.suspendedTimer !== null) {
      clearInterval(this.suspendedTimer);
      this.suspendedTimer = null;
    }
  }

  /**
   * Tier 4 finale — parte 2: GIF + messaggio personale come reward
   * finale del percorso completo. Auto-close dopo 10s o click
   * sull'X. Solo se `config` ha gif+message configurati.
   */
  private enterSecretFinale(): void {
    // Se non c'è config personalizzata, salta direttamente al finish
    // (il "gioco" era comunque autoironico, non serve reward).
    if (!this.config.gifPath && !this.config.personalMessage) {
      this.finish();
      return;
    }
    this.state.set('secret');
    this.phaseTimer = window.setTimeout(
      () => this.finish(),
      SECRET_FINALE_DURATION_MS,
    );
  }

  private finish(): void {
    this.cleanup();
    this.state.set('idle');
  }

  private cleanup(): void {
    this.stopCountdown();
    this.stopSuspended();
    if (this.phaseTimer !== null) {
      clearTimeout(this.phaseTimer);
      this.phaseTimer = null;
    }
    this.keyBuffer = '';
  }

  // ─────────────────────────────────────────────────────────────────────
  // Keyboard listener globale
  // ─────────────────────────────────────────────────────────────────────

  private attachKeyboardListener(): void {
    if (this.keyListener) return;
    this.keyListener = (e: KeyboardEvent) => this.onKeydown(e);
    window.addEventListener('keydown', this.keyListener);
  }

  private detachKeyboardListener(): void {
    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener);
      this.keyListener = null;
    }
  }

  /**
   * Handler globale keyboard. Ignora:
   *   - eventi da input/textarea/contenteditable (utente sta typing
   *     in un form)
   *   - tasti non-carattere (Enter, Tab, frecce ecc.)
   *   - modificatori attivi (Ctrl/Alt/Meta) — sono shortcut, non
   *     testo
   *   - se già in stato non-idle (un modale alla volta)
   *
   * 🚨 FIX v2.14.8 (2026-07-20): guardia difensiva su `e.key`. Alcuni
   * KeyboardEvent hanno `e.key === undefined` (osservato con
   * browser autofill DevTools, autofill Chrome dei form, tasti
   * mediali/Chromecast su MacBook Touch Bar, e input methods
   * asiatici composing). Pre-fix, `e.key.length` scoppiava con
   *   `TypeError: Cannot read properties of undefined (reading 'length')`
   * ad ogni evento problematico — visibile a ogni keydown quando
   * l'utente stava digitando la password nel login (console
   * inondata di errori). Ora la guardia esce silenziosamente.
   */
  private onKeydown(e: KeyboardEvent): void {
    if (this.state() !== 'idle') return;
    if (e.ctrlKey || e.altKey || e.metaKey) return;
    if (!e.key || e.key.length !== 1) return;

    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }
    }

    this.keyBuffer = (this.keyBuffer + e.key).slice(-32).toLowerCase();

    if (this.keyBuffer.includes(this.config.secretWord.toLowerCase())) {
      this.keyBuffer = ''; // reset così un tasto successivo non re-attiva
      this.arm();
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // sessionStorage helpers (safe against private mode / storage disabled)
  // ─────────────────────────────────────────────────────────────────────

  private readClickCount(): number {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return 0;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return 0;
    }
  }

  private writeClickCount(value: number): void {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // no-op — silent fail se sessionStorage non è disponibile
    }
  }
}
